import Slide from '@/components/shared/Slide';
import StatCard from '@/components/shared/StatCard';
import { Separator } from '@/components/ui/separator';
import type { RoadmapCompanyConfig, ViewMode } from '@/types/roadmap.types';

interface Props {
  config: RoadmapCompanyConfig;
  mode: ViewMode;
}

export default function CompanySnapshotSlide({ config, mode }: Props) {
  const { company, investment_layers } = config;

  const hqDisplay = [
    company.headquarters.city,
    company.headquarters.state,
    company.headquarters.country,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <Slide mode={mode}>
      <div className="w-full max-w-7xl mx-auto">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
          Company Snapshot
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-10">
          {company.name}
        </h2>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10">
          <StatCard value={company.industry} label="Industry" accent="primary" />
          <StatCard value={hqDisplay} label="Headquarters" accent="muted" />
          <StatCard value={`TRL ${company.trl} / 9`} label="Readiness Level" accent="blue" />
          <StatCard value={company.employee_count} label="Employees" accent="muted" />
          <StatCard value={company.founded_year} label="Founded" accent="muted" />
          <StatCard
            value={company.development_status.replace('-', ' ')}
            label="Status"
            accent="secondary"
            className="capitalize"
          />
        </div>

        <Separator className="mb-8" />

        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-4">
          Investment Layers
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          {investment_layers.map((layer) => (
            <div
              key={layer.id}
              className="flex items-start gap-3 rounded-xl border border-border/50 p-4"
            >
              <span
                className={`mt-0.5 w-2.5 h-2.5 rounded-full shrink-0 ${
                  layer.priority === 'high'
                    ? 'bg-emerald-500'
                    : layer.priority === 'medium'
                      ? 'bg-amber-400'
                      : 'bg-muted-foreground/40'
                }`}
              />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">
                  {layer.label}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  {layer.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Slide>
  );
}
